import { graphql } from "babel-plugin-relay/macro";
import { useFragment, useMutation } from "react-relay";
import { useEffect, useState } from "react";
import { TextWithTooltip } from "../../components/TextWithTooltip";
import { Button, Dropdown } from "react-bootstrap";
import { selfServiceStatusSetStatusMutation } from "./__generated__/selfServiceStatusSetStatusMutation.graphql";
import {
    UJStatuses,
    selfServiceStatus_statusFragment$key,
} from "./__generated__/selfServiceStatus_statusFragment.graphql";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { SuccessToast, ErrorToast } from "./selfServiceSettings";
import styles from "./selfServiceSettings.module.scss";

const SelfServiceSettingsStatusFragment = graphql`
    fragment selfServiceStatus_statusFragment on UserJourney {
        id
        status
    }
`;

export function SelfServiceStatus({
    userJourney,
    ujID,
}: {
    userJourney: selfServiceStatus_statusFragment$key;
    ujID: number;
}) {
    const userJourneyStatus = useFragment(
        SelfServiceSettingsStatusFragment,
        userJourney,
    );

    const [setStatus] = useMutation<selfServiceStatusSetStatusMutation>(graphql`
        mutation selfServiceStatusSetStatusMutation(
            $ujID: Int!
            $status: UJStatuses!
        ) {
            setSelfServiceUJStatus(uj: { id: $ujID, status: $status }) {
                userJourney {
                    ...selfServiceStatus_statusFragment
                }
            }
        }
    `);

    const [showSuccess, setShowSuccess] = useState(false);
    const [showFailure, setShowFailure] = useState(false);

    type KnownUJStatuses = Exclude<UJStatuses, "%future added value">;
    const ujStatusMapping: Record<
        KnownUJStatuses | "UNKNOWN",
        {
            displayStatus: string;
            variant: string;
        }
    > = {
        RUNNING: {
            displayStatus: "Running",
            variant: "success",
        },
        PAUSED: {
            displayStatus: "Paused",
            variant: "warning",
        },
        OBSOLETE: {
            displayStatus: "Stopped",
            variant: "danger",
        },
        UNKNOWN: {
            displayStatus: "Unknown",
            variant: "secondary",
        },
    };

    let currentStatusKey: UJStatuses | "UNKNOWN" = userJourneyStatus.status;
    if (currentStatusKey === "%future added value") {
        // support for newer UJ statuses that are not present
        // we don't expect these values yet, but we need to set it to something to avoid errors
        currentStatusKey = "UNKNOWN";
    }

    const [selectedStatusKey, setSelectedStatusKey] = useState<
        KnownUJStatuses | "UNKNOWN"
    >(currentStatusKey as KnownUJStatuses | "UNKNOWN");

    useEffect(() => {
        setSelectedStatusKey(currentStatusKey as KnownUJStatuses | "UNKNOWN");
    }, [currentStatusKey]);

    useEffect(() => {
        setShowFailure(false);
        setShowSuccess(false);
    }, [ujID]);

    return (
        <div>
            <div className={styles.statusSectionInner}>
                <TextWithTooltip name="Journey Status" />
                <Dropdown
                    onSelect={(eventKey) => {
                        if (eventKey !== null) {
                            setSelectedStatusKey(eventKey as KnownUJStatuses);
                        }
                    }}
                >
                    <Dropdown.Toggle
                        title={ujStatusMapping[selectedStatusKey].displayStatus}
                        variant={ujStatusMapping[selectedStatusKey].variant}
                    >
                        {ujStatusMapping[selectedStatusKey].displayStatus}

                        <FontAwesomeIcon
                            style={{ marginLeft: "1em" }}
                            icon={faChevronDown}
                        />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item
                            active={selectedStatusKey === "RUNNING"}
                            eventKey="RUNNING"
                        >
                            Running
                        </Dropdown.Item>
                        <Dropdown.Item
                            active={selectedStatusKey === "PAUSED"}
                            eventKey="PAUSED"
                        >
                            Paused
                        </Dropdown.Item>
                        <Dropdown.Item
                            active={selectedStatusKey === "OBSOLETE"}
                            eventKey="OBSOLETE"
                        >
                            Stopped
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <Button
                variant="tribe-primary"
                className={styles.saveButton}
                onClick={() => {
                    if (
                        selectedStatusKey &&
                        selectedStatusKey !== currentStatusKey
                    ) {
                        setStatus({
                            variables: {
                                ujID,
                                status: selectedStatusKey as KnownUJStatuses,
                            },
                            onCompleted(_, errors) {
                                if (errors && errors.length > 0) {
                                    setShowFailure(true);
                                } else {
                                    setShowSuccess(true);
                                }
                            },
                            onError(_) {
                                setShowFailure(true);
                            },
                        });
                    }
                }}
                disabled={selectedStatusKey === currentStatusKey}
            >
                Save Changes
            </Button>

            {showSuccess && (
                <SuccessToast clearToast={() => setShowSuccess(false)} />
            )}

            {showFailure && (
                <ErrorToast
                    errorMessage={"Failed to update status, please try again."}
                    clearToast={() => setShowFailure(false)}
                />
            )}
        </div>
    );
}
